import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import type { MessageInstance } from 'antd/es/message/interface';
import { handleApiError } from '@shared/utils/errorHandler';

/**
 * Опции для useApiNotifications хука
 */
export interface UseApiNotificationsOptions {
  /** Сообщение при успехе (i18n ключ или текст) */
  successMessage?: string;

  /** Использовать Notification вместо Message для успеха? */
  useNotificationOnSuccess?: boolean;

  /** Namespace для переводов ошибок */
  namespace?: string;

  /** Маппинг кодов ошибок на i18n ключи */
  errorCodeMap?: Record<string, string>;

  /** Использовать Notification вместо Message для ошибок? */
  useNotificationOnError?: boolean;
}

/**
 * Переиспользуемый хук для обработки API ошибок и успехов
 * ✅ Поддерживает i18n
 * ✅ Поддерживает динамические темы
 * ✅ Маппинг кодов ошибок на переводы
 *
 * @example
 * ```typescript
 * const { onSuccess, onError } = useApiNotifications({
 *   successMessage: 'customers:modals.create.success',
 *   namespace: 'customers',
 *   errorCodeMap: {
 *     'duplicate_username': 'customers:errors.duplicate_username',
 *   },
 * });
 *
 * const mutation = useMutation({
 *   onSuccess: () => onSuccess(),
 *   onError: (error) => onError(error),
 * });
 * ```
 */
export function useApiNotifications(options: UseApiNotificationsOptions = {}) {
  const { message } = App.useApp();
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const {
    successMessage,
    useNotificationOnSuccess = false,
    namespace,
    errorCodeMap = {},
    useNotificationOnError = false,
  } = options;

  const handleSuccess = (msg?: string) => {
    const text = msg || successMessage || t('common.success');

    if (useNotificationOnSuccess) {
      notification.success({
        title: t('common.success'),
        description: text,
        duration: 3,
      });
    } else {
      message.success(text);
    }
  };

  const handleError = (error: unknown, fallback?: string) => {
    handleApiError(error, {
      t,
      namespace,
      fallbackKey: fallback || 'common.error',
      errorCodeMap,
      messageApi: message,
      notificationApi: notification,
      useNotificationForAll: useNotificationOnError,
    });
  };

  return {
    /**
     * Показать сообщение об успехе
     */
    onSuccess: handleSuccess,

    /**
     * Показать сообщение об ошибке
     */
    onError: handleError,

    /**
     * Получить callbacks для mutation
     */
    getMutationCallbacks: () => ({
      onSuccess: () => handleSuccess(),
      onError: (error: unknown) => handleError(error),
    }),
  };
}
